<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import Section from "../internal/Section.vue";
import EditableField from "../fields/EditableField.vue";
import ReadonlyField from "../fields/ReadonlyField.vue";
import { Badge } from "@/shared/ui/badge";

const { t } = useI18n();

defineProps({
  data: { type: Object, required: true },
  editMode: { type: Boolean, default: false },
  isOwn: { type: Boolean, default: false },
});

const emit = defineEmits(["update"]);
function setField(key, value) {
  emit("update", key, value);
}

const LICENSE_STATUS_LABELS = {
  VALID: "Действующая",
  SUSPENDED: "Приостановлена",
  TERMINATED: "Прекращена",
};
const LICENSE_STATUSES = computed(() =>
  Object.entries(LICENSE_STATUS_LABELS).map(([value, label]) => ({
    value,
    label: t(label),
  }))
);
function licenseStatusLabel(code) {
  return LICENSE_STATUS_LABELS[code] || code || "Не указано";
}
</script>

<template>
  <Section
    icon="sli-medal"
    :title="$t('Лицензия')"
    :sub="$t('Сведения о государственной лицензии')"
  >
    <template #actions>
      <Badge
        :class="
          data.license_status === 'VALID'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-muted text-muted-foreground border border-border'
        "
      >
        {{ $t(licenseStatusLabel(data.license_status)) }}
      </Badge>
    </template>

    <div
      class="grid grid-cols-3 gap-x-5 gap-y-3.5 max-md:grid-cols-2 max-sm:grid-cols-1"
    >
      <template v-if="editMode">
        <EditableField
          :label="$t('Номер лицензии')"
          required
          :model-value="data.license_number"
          @update:model-value="setField('license_number', $event)"
        />
        <EditableField
          :label="$t('Дата выдачи')"
          type="date"
          size="sm"
          required
          :model-value="data.license_issue_date"
          @update:model-value="setField('license_issue_date', $event)"
        />
        <ReadonlyField
          :label="$t('Кем выдана')"
          :value="data.license_issued_by"
        />
        <EditableField
          :label="$t('Статус лицензии')"
          required
          :model-value="data.license_status"
          :options="LICENSE_STATUSES"
          @update:model-value="setField('license_status', $event)"
        />
        <EditableField
          :label="$t('Причина статуса')"
          :model-value="data.license_status_reason"
          @update:model-value="setField('license_status_reason', $event)"
        />
        <EditableField
          :label="$t('Дата начала статуса')"
          type="date"
          size="sm"
          :model-value="data.license_status_start_date"
          @update:model-value="setField('license_status_start_date', $event)"
        />
      </template>
      <template v-else>
        <ReadonlyField
          :label="$t('Номер лицензии')"
          :value="data.license_number"
        />
        <ReadonlyField
          :label="$t('Дата выдачи')"
          :value="data.license_issue_date"
          type="date"
          size="sm"
        />
        <ReadonlyField
          :label="$t('Кем выдана')"
          :value="data.license_issued_by"
        />
        <ReadonlyField
          :label="$t('Статус лицензии')"
          :value="$t(licenseStatusLabel(data.license_status))"
        />
        <ReadonlyField
          :label="$t('Причина статуса')"
          :value="data.license_status_reason"
        />
        <ReadonlyField
          :label="$t('Дата начала статуса')"
          :value="data.license_status_start_date"
          type="date"
          size="sm"
        />
      </template>
    </div>
  </Section>

  <Section
    icon="huge-key"
    :title="$t('Блокировки')"
    :sub="$t('Активные ограничения по нотариусу')"
    icon-bg="bg-red-50"
    icon-color="text-red-600"
  >
    <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
      <template v-if="editMode">
        <EditableField
          :label="$t('Описание')"
          type="textarea"
          full
          :placeholder="$t('Причина блокировки…')"
          :model-value="data.block_description"
          @update:model-value="setField('block_description', $event)"
        />
        <EditableField
          :label="$t('Действует с')"
          type="date"
          size="sm"
          :model-value="data.block_start_date"
          @update:model-value="setField('block_start_date', $event)"
        />
        <EditableField
          :label="$t('Действует по')"
          type="date"
          size="sm"
          :model-value="data.block_end_date"
          @update:model-value="setField('block_end_date', $event)"
        />
      </template>
      <template v-else>
        <ReadonlyField
          :label="$t('Описание')"
          :value="data.block_description"
          full
        />
        <ReadonlyField
          :label="$t('Действует с')"
          :value="data.block_start_date"
          type="date"
          size="sm"
        />
        <ReadonlyField
          :label="$t('Действует по')"
          :value="data.block_end_date"
          type="date"
          size="sm"
        />
      </template>
    </div>
  </Section>
</template>
